import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";

const initialSummary = {
  clients: 0,
  activeProjects: 0,
  outstandingInvoices: 0,
  outstandingBalance: 0,
  openTasks: 0,
  draftProposals: 0,
};

function toMoney(value) {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return 0;
  }

  return amount;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(toMoney(value));
}

function DevelopmentDashboard() {
  const [summary, setSummary] =
    useState(initialSummary);

  const [recentProjects, setRecentProjects] =
    useState([]);

  const [recentInvoices, setRecentInvoices] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setIsLoading(true);
      setError("");

      try {
        const [
          clientResponse,
          projectResponse,
          invoiceResponse,
          proposalResponse,
          taskSummaryResponse,
        ] = await Promise.all([
          Api.getDevelopmentClients(),
          Api.getDevelopmentProjects(),
          Api.getDevelopmentInvoices(),
          Api.getDevelopmentProposals(),
          Api.getDevelopmentProjectTaskSummary(),
        ]);

        if (!active) {
          return;
        }

        const clients =
          clientResponse?.clients || [];

        const projects =
          projectResponse?.projects || [];

        const invoices =
          invoiceResponse?.invoices || [];

        const proposals =
          proposalResponse?.proposals || [];

        const taskSummary =
          taskSummaryResponse?.summary || {};

        const activeProjects = projects.filter(
          (project) =>
            Number(project.active) === 1 &&
            Number(project.archived) !== 1 &&
            ![
              "completed",
              "cancelled",
            ].includes(
              String(
                project.status || ""
              ).toLowerCase()
            )
        );

        const outstandingInvoices =
          invoices.filter(
            (invoice) =>
              Number(invoice.paid) !== 1 &&
              String(
                invoice.status || ""
              ).toLowerCase() !==
                "cancelled"
          );

        const outstandingBalance =
          outstandingInvoices.reduce(
            (total, invoice) =>
              total +
              toMoney(
                invoice.balance_due
              ),
            0
          );

        const draftProposals =
          proposals.filter(
            (proposal) =>
              String(
                proposal.status || ""
              ).toLowerCase() ===
              "draft"
          );

        setSummary({
          clients: clients.length,
          activeProjects:
            activeProjects.length,
          outstandingInvoices:
            outstandingInvoices.length,
          outstandingBalance,
          openTasks:
            Number(
              taskSummary.open_tasks
            ) || 0,
          draftProposals:
            draftProposals.length,
        });

        setRecentProjects(
          [...projects]
            .sort(
              (a, b) =>
                new Date(
                  b.updated_at ||
                    b.created_at ||
                    0
                ) -
                new Date(
                  a.updated_at ||
                    a.created_at ||
                    0
                )
            )
            .slice(0, 5)
        );

        setRecentInvoices(
          [...invoices]
            .sort(
              (a, b) =>
                new Date(
                  b.updated_at ||
                    b.created_at ||
                    0
                ) -
                new Date(
                  a.updated_at ||
                    a.created_at ||
                    0
                )
            )
            .slice(0, 5)
        );
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(
          requestError.message ||
            "The Development dashboard could not be loaded."
        );
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      active = false;
    };
  }, []);

  const cards = useMemo(
    () => [
      {
        label: "Clients",
        value: summary.clients,
        path: "/avwakindevelopment/admin/clients",
      },
      {
        label: "Active projects",
        value: summary.activeProjects,
        path: "/avwakindevelopment/admin/projects",
      },
      {
        label: "Draft proposals",
        value: summary.draftProposals,
        path: "/avwakindevelopment/admin/proposals",
      },
      {
        label: "Open tasks",
        value: summary.openTasks,
        path: "/avwakindevelopment/admin/projects",
      },
      {
        label: "Outstanding invoices",
        value:
          summary.outstandingInvoices,
        path: "/avwakindevelopment/admin/invoices",
      },
      {
        label: "Outstanding balance",
        value: formatCurrency(
          summary.outstandingBalance
        ),
        path: "/avwakindevelopment/admin/payments",
      },
    ],
    [summary]
  );

  if (isLoading) {
    return (
      <div
        className="development-admin-state"
        role="status"
      >
        Loading Development dashboard...
      </div>
    );
  }

  return (
    <div className="development-dashboard">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Overview
          </p>

          <h2>Dashboard</h2>

          <p>
            Manage Development clients, projects,
            proposals, billing, documents, and
            deliverables.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <Link
            to="/avwakindevelopment/admin/clients"
            className="development-admin-button secondary"
          >
            View clients
          </Link>

          <Link
            to="/avwakindevelopment/admin/projects"
            className="development-admin-button primary"
          >
            View projects
          </Link>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      <section className="development-summary-grid">
        {cards.map((card) => (
          <Link
            key={card.label}
            to={card.path}
            className="development-summary-card"
          >
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </Link>
        ))}
      </section>

      <section className="development-dashboard-grid">
        <article className="development-admin-panel">
          <div className="development-admin-panel-heading">
            <div>
              <p className="development-admin-eyebrow">
                Recent activity
              </p>

              <h3>Projects</h3>
            </div>

            <Link to="/avwakindevelopment/admin/projects">
              View all
            </Link>
          </div>

          {recentProjects.length === 0 ? (
            <div className="development-admin-empty">
              No projects have been created yet.
            </div>
          ) : (
            <div className="development-admin-list">
              {recentProjects.map((project) => (
                <div
                  key={project.id}
                  className="development-admin-list-row"
                >
                  <div>
                    <strong>
                      {project.title}
                    </strong>

                    <span>
                      {project.business_name ||
                        project.client_name ||
                        "Client"}
                    </span>
                  </div>

                  <span className="development-admin-status">
                    {project.status ||
                      "draft"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="development-admin-panel">
          <div className="development-admin-panel-heading">
            <div>
              <p className="development-admin-eyebrow">
                Billing
              </p>

              <h3>Invoices</h3>
            </div>

            <Link to="/avwakindevelopment/admin/invoices">
              View all
            </Link>
          </div>

          {recentInvoices.length === 0 ? (
            <div className="development-admin-empty">
              No invoices have been created yet.
            </div>
          ) : (
            <div className="development-admin-list">
              {recentInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="development-admin-list-row"
                >
                  <div>
                    <strong>
                      {invoice.invoice_number}
                    </strong>

                    <span>
                      {invoice.business_name ||
                        invoice.project_title ||
                        "Invoice"}
                    </span>
                  </div>

                  <div className="development-admin-list-value">
                    <strong>
                      {formatCurrency(
                        invoice.balance_due
                      )}
                    </strong>

                    <span>
                      {invoice.status ||
                        "draft"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>
    </div>
  );
}

export default DevelopmentDashboard;
